import type { WebUser } from "@/types/WebUser";
import { type AuthHeader, authHeader } from "@roman/shared/lib/auth/authHeader";
import romanApi from "@roman/shared/lib/net/romanApi";
import type { GoogleLoginCallback } from "@roman/shared/types/Google";
import { toast } from "sonner";
import { mutate } from "swr";
import { create } from "zustand";

export type AuthState =
  | "unknown"
  | "authenticated"
  | "unauthenticated"
  | "loggingIn"
  | "loggingOut"
  | "refreshingToken"
  | "fetchingUser";

export type AuthStoreState = {
  authState: AuthState;
  user: WebUser | null;
  isNewUser: boolean;
  setIsNewUser: (isNewUser: boolean) => void; // Currently, we cannot remove this function yet
};

export type AuthStoreActions = {
  loginWithGoogle: (trigger: GoogleLoginCallback) => Promise<void>;
  refreshToken: () => Promise<void>;
  fetchAndSetUser: () => Promise<void>;
  logout: () => void;
  deleteMe: (headers: AuthHeader) => Promise<void>;
  refetch: () => Promise<void>;
};

export const useAuthStore = create<AuthStoreState & AuthStoreActions>()((set, get) => {
  const _setAuthState = (state: AuthState) => set({ authState: state });
  const _setUser = (user: WebUser) => set({ user });
  const _clearUser = () => {
    set({ user: null, authState: "unauthenticated" });
    authHeader.clear();
    localStorage.removeItem("loginAt");
  };
  const _setTokenAndLoginAt = (token: string) => {
    authHeader.set(token);
    localStorage.setItem("loginAt", JSON.stringify({ loginAt: new Date().toISOString() }));
  };

  const _fetchUser = async () => {
    const headers = authHeader.getOrThrow();
    const response = await romanApi.get<{ user: WebUser }>("/user/me", undefined, { headers });
    return response.user;
  };

  return {
    authState: "unknown",
    user: null,
    isNewUser: false,
    setIsNewUser: (isNewUser: boolean) => set({ isNewUser }),

    loginWithGoogle: async (trigger: GoogleLoginCallback) => {
      const { setIsNewUser, fetchAndSetUser, authState } = get();

      if (authState === "loggingIn") {
        return;
      }

      _setAuthState("loggingIn");

      await trigger({
        onSuccess: async (data) => {
          _setTokenAndLoginAt(data.accessToken);
          setIsNewUser(data.isFirstTime);
          await fetchAndSetUser();
        },
        onError: () => {
          _clearUser();
          toast.error("ログインに失敗しました");
        },
      });
    },

    fetchAndSetUser: async () => {
      _setAuthState("fetchingUser");
      try {
        const user = await _fetchUser();
        _setUser(user);
        _setAuthState("authenticated");
      } catch (error) {
        _setAuthState("unauthenticated");
        _clearUser();
      }
    },

    refreshToken: async () => {
      try {
        const response = await romanApi.post<{ accessToken: string }, void>("/auth/refresh", undefined, {
          withCredentials: true,
        });

        _setTokenAndLoginAt(response.accessToken);
        await get().fetchAndSetUser();
      } catch (error) {
        _clearUser();
      }
    },

    logout: async () => {
      if (get().authState === "loggingOut") {
        return;
      }

      _setAuthState("loggingOut");

      try {
        await romanApi.delete("/auth/logout", { withCredentials: true });
        _clearUser();
        mutate(() => true, undefined, { revalidate: false });
        window.location.href = "/login"; // useNavigate does not work here
      } catch (error) {
        toast.error("ログアウトに失敗しました");
      }
    },

    deleteMe: async (headers: AuthHeader) => {
      if (get().authState === "loggingOut") {
        return;
      }
      _setAuthState("loggingOut");
      try {
        await romanApi.post("/user-delete-requests", {}, { headers, withCredentials: true });
        _clearUser();
        mutate(() => true, undefined, { revalidate: false });
      } catch (error) {
        toast.error("ユーザーの削除に失敗しました");
      }
    },

    refetch: async () => {
      const user = await _fetchUser();
      _setUser(user);
    },
  };
});

export const isUnknownAuthState = (authState: AuthState) => {
  return authState === "unknown" || authState === "refreshingToken" || authState === "fetchingUser";
};

export const tryGetAuthenticatedUser = (): WebUser | null => useAuthStore((state) => state.user);

export const getAuthenticatedUser = (): WebUser => {
  const user = tryGetAuthenticatedUser();

  if (!user) {
    throw new Error("You must use this function inside a protected route");
  }

  return user;
};
